import React, { useEffect, useState, Fragment } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, Shield, Lock, ChevronRight, ChevronDown, CheckCircle2, Sparkles, Clock, Video, FileText, Star, Package, Headphones, Gift, ArrowLeft, Users, LogIn, UserPlus, Trash2, ShoppingCart, Check, Info, Crown } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { Breadcrumbs } from '../components/ui/Breadcrumbs';
import { useToast } from '../components/ui/Toast';
import { useCart } from '../contexts/CartContext';
import { useLanguage } from '../i18n/LanguageContext';
import { useUser } from '../contexts/UserContext';
import { usePackages } from '../contexts/PackagesContext';
interface CheckoutPageProps {
  onNavigate: (page: string) => void;
}
interface AddOnService {
  id: string;
  title: string;
  price: number;
}
interface AddOnCategory {
  id: string;
  title: string;
  icon: React.ElementType;
  color: string;
  services: AddOnService[];
  isNavigable?: boolean;
}
const ALL_ADDON_CATEGORIES: AddOnCategory[] = [{
  id: 'reports',
  title: 'Отчёты',
  icon: FileText,
  color: 'indigo',
  services: [{
    id: 'addon-natal',
    title: 'Натальная карта Premium',
    price: 3900
  }, {
    id: 'addon-compatibility',
    title: 'Совместимость Premium',
    price: 4900
  }, {
    id: 'addon-solar',
    title: 'Соляр Premium',
    price: 4400
  }, {
    id: 'addon-forecast',
    title: 'Прогноз (Транзиты) Premium',
    price: 3900
  }, {
    id: 'addon-combo',
    title: 'Комбо: Натал + Соляр',
    price: 6900
  }]
}, {
  id: 'consultations',
  title: 'Консультации',
  icon: Headphones,
  color: 'sky',
  services: [],
  isNavigable: true
}, {
  id: 'packages',
  title: 'Пакеты',
  icon: Gift,
  color: 'emerald',
  services: [{
    id: 'addon-pkg20',
    title: 'Пакет 20 отчётов',
    price: 76000
  }, {
    id: 'addon-pkg50',
    title: 'Пакет 50 отчётов',
    price: 180000
  }, {
    id: 'addon-pkg100',
    title: 'Пакет 100 отчётов',
    price: 315000
  }]
}];
// Helper to get all flat services for price calculation
const ALL_FLAT_SERVICES = ALL_ADDON_CATEGORIES.flatMap(c => c.services);
export function CheckoutPage({
  onNavigate
}: CheckoutPageProps) {
  const {
    success
  } = useToast();
  const {
    t
  } = useLanguage();
  const {
    items,
    removeItem,
    mainItem,
    clearCart
  } = useCart();
  const {
    activePackages,
    totalRemaining,
    useReport,
    getOldestActivePackage
  } = usePackages();
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'sbp'>('card');
  const [isProcessing, setIsProcessing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoDiscount, setPromoDiscount] = useState(0);
  const [selectedAddOns, setSelectedAddOns] = useState<string[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const {
    isLoggedIn,
    user
  } = useUser();
  // Subscription discount for astrologers
  const isAstrologer = user?.role === 'astrologer';
  const currentPlan = isAstrologer ? 'PRO' : 'FREE';
  const subDiscount = currentPlan === 'TOP' ? 20 : currentPlan === 'PRO' ? 10 : 0;
  // Collapsible state for categories (left)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['reports']));
  // Collapsible state for cart items (right)
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  // Initialize expanded items based on cart items
  useEffect(() => {
    if (items.length > 0) {
      setExpandedItems(new Set([items[0].id]));
    }
  }, [items.length]);
  // Use all cart items for pricing
  const basePrice = items.length > 0 ? items.reduce((sum, i) => sum + i.price, 0) : 0;
  const toggleAddOn = (id: string) => {
    setSelectedAddOns(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };
  const toggleCategory = (id: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const toggleCartItem = (id: string) => {
    if (items.length <= 1) return; // Don't collapse if only 1 item
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const addOnsTotal = ALL_FLAT_SERVICES.filter(s => selectedAddOns.includes(s.id)).reduce((sum, s) => sum + s.price, 0);
  const subDiscountAmount = subDiscount > 0 ? Math.round((basePrice + addOnsTotal) * subDiscount / 100) : 0;
  const subtotal = basePrice + addOnsTotal;
  const promoDiscountAmount = promoApplied ? promoDiscount : 0;
  const discount = subDiscountAmount + promoDiscountAmount;
  const total = Math.max(0, basePrice + addOnsTotal - discount);
  // Package payment logic
  const hasActivePackage = activePackages.length > 0;
  const canPayWithPackage = hasActivePackage && items.every(item => item.type === 'premium-report' || item.type === 'consultation');
  const [payWithPackage, setPayWithPackage] = useState(canPayWithPackage);
  const handleApplyPromo = () => {
    if (promoCode.trim().toLowerCase() === 'astro10') {
      setPromoDiscount(Math.round(subtotal * 0.1));
      setPromoApplied(true);
    } else if (promoCode.trim().toLowerCase() === 'astro20') {
      setPromoDiscount(Math.round(subtotal * 0.2));
      setPromoApplied(true);
    } else {
      setPromoApplied(true);
      setPromoDiscount(500);
    }
  };
  const handlePayment = (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreedToTerms || !isLoggedIn) return;
    setIsProcessing(true);
    setTimeout(() => {
      setIsProcessing(false);
      if (payWithPackage && canPayWithPackage) {
        const pkg = getOldestActivePackage();
        if (pkg) {
          items.forEach(item => {
            useReport(pkg.id, {
              orderId: `ord-${Date.now()}`,
              reportType: item.type === 'consultation' ? 'consultation' : 'report',
              reportTitle: item.name,
              source: item.astrologer ? 'astrologer' : 'ai',
              astrologerName: item.astrologer?.name
            });
          });
          success('Оплачено из пакета отчетов');
        }
      } else {
        success(t('checkout.success'));
      }
      clearCart();
      onNavigate('order-confirmation');
    }, 1500);
  };
  const steps = [{
    label: t('checkout.step.product'),
    done: true
  }, {
    label: t('checkout.step.checkout'),
    active: true
  }, {
    label: t('checkout.step.payment'),
    done: false
  }];
  const formatPrice = (p: number) => p.toLocaleString('ru-RU') + ' ₽';
  const getColorClasses = (color: string) => {
    switch (color) {
      case 'indigo':
        return 'text-indigo-600 bg-indigo-100 border-indigo-200';
      case 'sky':
        return 'text-sky-600 bg-sky-100 border-sky-200';
      case 'emerald':
        return 'text-emerald-600 bg-emerald-100 border-emerald-200';
      case 'amber':
        return 'text-amber-600 bg-amber-100 border-amber-200';
      default:
        return 'text-slate-600 bg-slate-100 border-slate-200';
    }
  };
  return <div className="w-full bg-slate-50 min-h-screen pb-32 lg:pb-24" data-id="element-21968">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 pt-6 pb-6" data-id="element-21969">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-21970">
          <Breadcrumbs items={[{
          label: t('checkout.breadcrumb.home'),
          onClick: () => onNavigate('home')
        }, {
          label: t('checkout.breadcrumb.marketplace'),
          onClick: () => onNavigate('marketplace')
        }, {
          label: t('checkout.breadcrumb.title')
        }]} className="mb-6" data-id="element-21971" />

          <div className="flex items-center justify-between mb-6" data-id="element-21972">
            <div className="flex items-center gap-3" data-id="element-21973">
              <button onClick={() => onNavigate('product-detail')} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:border-slate-300 transition-colors" aria-label={t('checkout.back')} data-id="element-21974">
                <ArrowLeft className="w-4 h-4" data-id="element-21975" />
              </button>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900" data-id="element-21976">
                {t('checkout.title')}
              </h1>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2" data-id="element-21977">
            {steps.map((step, i) => <Fragment key={step.label} data-id="element-21978">
                <div className="flex items-center gap-2" data-id="element-21979">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step.done ? 'bg-emerald-500 text-white' : step.active ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-21980">
                    {step.done ? <CheckCircle2 className="w-4 h-4" data-id="element-21981" /> : i + 1}
                  </div>
                  <span className={`text-sm font-medium ${step.active ? 'text-slate-900' : step.done ? 'text-emerald-600' : 'text-slate-400'}`} data-id="element-21982">
                    {step.label}
                  </span>
                </div>
                {i < steps.length - 1 && <div className={`flex-1 h-0.5 mx-2 rounded ${step.done ? 'bg-emerald-400' : 'bg-slate-200'}`} data-id="element-21983" />}
              </Fragment>)}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8" data-id="element-21984">
        {subDiscount > 0 && <div className="mb-6 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl flex items-center gap-3" data-id="element-21985">
            <Crown className="w-5 h-5 text-indigo-600 flex-shrink-0" data-id="element-21986" />
            <div data-id="element-21987">
              <span className="font-bold text-slate-900" data-id="element-21988">
                Тариф {currentPlan}
              </span>
              <span className="text-sm text-slate-600 ml-2" data-id="element-21989">
                — скидка{' '}
                <span className="font-bold text-indigo-600" data-id="element-21990">
                  {subDiscount}%
                </span>{' '}
                на весь заказ применена автоматически
              </span>
            </div>
          </div>}
        <form onSubmit={handlePayment} className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-21991">
          {/* Left Column */}
          <div className="lg:col-span-7 space-y-6" data-id="element-21992">
            {/* Add-on Services (Collapsible) */}
            <Card padding="lg" data-id="element-21993">
              <div className="flex items-center gap-2 mb-2" data-id="element-21994">
                <Sparkles className="w-5 h-5 text-amber-500" data-id="element-21995" />
                <h2 className="text-xl font-bold text-slate-900" data-id="element-21996">
                  {t('checkout.addons.title')}
                </h2>
              </div>
              <p className="text-sm text-slate-500 mb-6" data-id="element-21997">
                {t('checkout.addons.subtitle')}
              </p>

              <div className="space-y-3" data-id="element-21998">
                {ALL_ADDON_CATEGORIES.map(category => {
                const Icon = category.icon;
                const isExpanded = expandedCategories.has(category.id);
                const selectedCount = category.services.filter(s => selectedAddOns.includes(s.id)).length;
                const colorClasses = getColorClasses(category.color);
                return <div key={category.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white" data-id="element-21999">
                      <button type="button" onClick={() => toggleCategory(category.id)} className="w-full flex items-center justify-between p-3.5 hover:bg-slate-50 transition-colors" data-id="element-22000">
                        <div className="flex items-center gap-3" data-id="element-22001">
                          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorClasses}`} data-id="element-22002">
                            <Icon className="w-4 h-4" data-id="element-22003" />
                          </div>
                          <span className="font-semibold text-slate-900 text-sm" data-id="element-22004">
                            {category.title}
                          </span>
                          {selectedCount > 0 && <span className="bg-indigo-100 text-indigo-700 text-[10px] font-bold px-2 py-0.5 rounded-full" data-id="element-22005">
                              {selectedCount}
                            </span>}
                        </div>
                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-22006" />
                      </button>

                      <AnimatePresence initial={false} data-id="element-22007">
                        {isExpanded && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2,
                      ease: 'easeInOut'
                    }} data-id="element-22008">
                            <div className="px-3 pb-3 pt-1 space-y-1.5 border-t border-slate-100" data-id="element-22009">
                              {(category as any).isNavigable ? <div className="p-3 text-center" data-id="element-22010">
                                  <p className="text-xs text-slate-500 mb-3" data-id="element-22011">
                                    Цена зависит от выбранного астролога и
                                    длительности
                                  </p>
                                  <button type="button" onClick={() => onNavigate('astrologers')} className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-sky-50 border border-sky-200 text-sky-700 hover:bg-sky-100 transition-colors text-sm font-medium" data-id="element-22012">
                                    <Users className="w-4 h-4" data-id="element-22013" />
                                    Выбрать астролога
                                  </button>
                                </div> : category.services.map(service => {
                          const isSelected = selectedAddOns.includes(service.id);
                          return <label key={service.id} className={`flex items-center justify-between p-2.5 rounded-lg cursor-pointer transition-colors ${isSelected ? 'bg-indigo-50/80 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'}`} data-id="element-22014">
                                      <div className="flex items-center gap-3 min-w-0" data-id="element-22015">
                                        <input type="checkbox" checked={isSelected} onChange={() => toggleAddOn(service.id)} className="w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 flex-shrink-0" data-id="element-22016" />
                                        <span className={`text-sm truncate ${isSelected ? 'font-medium text-indigo-900' : 'text-slate-700'}`} data-id="element-22017">
                                          {service.title}
                                        </span>
                                      </div>
                                      <span className={`text-sm font-medium tabular-nums whitespace-nowrap ml-3 ${isSelected ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-22018">
                                        +{formatPrice(service.price)}
                                      </span>
                                    </label>;
                        })}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </div>;
              })}
              </div>
            </Card>

            {/* Auth Gate */}
            {!isLoggedIn && <Card padding="lg" className="border-amber-200 bg-amber-50/30" data-id="element-22019">
                <div className="text-center py-4" data-id="element-22020">
                  <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4" data-id="element-22021">
                    <LogIn className="w-7 h-7 text-amber-600" data-id="element-22022" />
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-2" data-id="element-22023">
                    {t('checkout.auth.title')}
                  </h2>
                  <p className="text-sm text-slate-600 mb-6 max-w-sm mx-auto" data-id="element-22024">
                    {t('checkout.auth.desc')}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-xs mx-auto" data-id="element-22025">
                    <Button variant="primary" onClick={() => onNavigate('auth')} icon={<LogIn className="w-4 h-4" data-id="element-22027" />} className="flex-1" data-id="element-22026">
                      {t('checkout.auth.login')}
                    </Button>
                    <Button variant="outline" onClick={() => onNavigate('auth')} icon={<UserPlus className="w-4 h-4" data-id="element-22029" />} className="flex-1" data-id="element-22028">
                      {t('checkout.auth.register')}
                    </Button>
                  </div>
                  <p className="text-xs text-slate-500 mt-4 flex items-center justify-center gap-1" data-id="element-22030">
                    <Shield className="w-3 h-3" data-id="element-22031" />
                    {t('checkout.auth.hint')}
                  </p>
                </div>
              </Card>}

            {/* Payment Method */}
            <Card padding="lg" className={!isLoggedIn ? 'opacity-40 pointer-events-none select-none' : ''} data-id="element-22032">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-22033">
                {t('checkout.payment.title')}
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-22034">
                <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${paymentMethod === 'card' ? 'border-indigo-500 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-22035">
                  <input type="radio" name="payment" value="card" checked={paymentMethod === 'card'} onChange={() => setPaymentMethod('card')} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500" data-id="element-22036" />
                  <CreditCard className={`w-6 h-6 ml-3 mr-3 ${paymentMethod === 'card' ? 'text-indigo-600' : 'text-slate-400'}`} data-id="element-22037" />
                  <span className="font-medium text-slate-900" data-id="element-22038">
                    {t('checkout.payment.card')}
                  </span>
                </label>

                <label className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-colors ${paymentMethod === 'sbp' ? 'border-indigo-500 bg-indigo-50/30' : 'border-slate-200 hover:border-slate-300'}`} data-id="element-22039">
                  <input type="radio" name="payment" value="sbp" checked={paymentMethod === 'sbp'} onChange={() => setPaymentMethod('sbp')} className="w-4 h-4 text-indigo-600 border-slate-300 focus:ring-indigo-500" data-id="element-22040" />
                  <div className={`w-6 h-6 ml-3 mr-3 rounded bg-slate-100 flex items-center justify-center font-bold text-xs ${paymentMethod === 'sbp' ? 'text-indigo-600' : 'text-slate-500'}`} data-id="element-22041">
                    СБП
                  </div>
                  <span className="font-medium text-slate-900" data-id="element-22042">
                    {t('checkout.payment.sbp')}
                  </span>
                </label>
              </div>

              {paymentMethod === 'card' && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="space-y-4" data-id="element-22043">
                  <Input label={t('checkout.card.number')} placeholder="0000 0000 0000 0000" required data-id="element-22044" />
                  <div className="grid grid-cols-2 gap-4" data-id="element-22045">
                    <Input label={t('checkout.card.expiry')} placeholder="MM/YY" required data-id="element-22046" />
                    <Input label={t('checkout.card.cvc')} placeholder="123" type="password" maxLength={3} required data-id="element-22047" />
                  </div>
                  <Input label={t('checkout.card.name')} placeholder="IVAN IVANOV" required data-id="element-22048" />
                </motion.div>}

              {paymentMethod === 'sbp' && <motion.div initial={{
              opacity: 0,
              y: -8
            }} animate={{
              opacity: 1,
              y: 0
            }} className="text-center py-8" data-id="element-22049">
                  <div className="w-48 h-48 bg-slate-100 rounded-2xl mx-auto mb-4 flex items-center justify-center border-2 border-dashed border-slate-300" data-id="element-22050">
                    <span className="text-slate-400 font-medium" data-id="element-22051">
                      {t('checkout.sbp.qr')}
                    </span>
                  </div>
                  <p className="text-slate-600 text-sm" data-id="element-22052">
                    {t('checkout.sbp.hint')}
                  </p>
                </motion.div>}
            </Card>

            {/* Contact Info */}
            <Card padding="lg" className={!isLoggedIn ? 'opacity-40 pointer-events-none select-none' : ''} data-id="element-22053">
              <h2 className="text-xl font-bold text-slate-900 mb-6" data-id="element-22054">
                {t('checkout.contact.title')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-22055">
                <Input label={t('checkout.contact.name')} placeholder={t('checkout.contact.namePlaceholder')} required defaultValue="Анна" data-id="element-22056" />
                <Input label={t('checkout.contact.email')} type="email" placeholder="mail@example.com" required defaultValue="anna@example.com" data-id="element-22057" />
              </div>
              <Input label={t('checkout.contact.phone')} placeholder={t('checkout.contact.phonePlaceholder')} className="mt-4" data-id="element-22058" />
              <p className="text-xs text-slate-500 mt-4 flex items-center" data-id="element-22059">
                <Lock className="w-3 h-3 mr-1.5" data-id="element-22060" />{' '}
                {t('checkout.contact.privacy')}
              </p>
            </Card>
          </div>

          {/* Right Column: Order Summary */}
          <div className="lg:col-span-5" data-id="element-22061">
            <div className="sticky top-24 space-y-6" data-id="element-22062">
              <Card padding="lg" className="border-indigo-100 shadow-lg" data-id="element-22063">
                <h3 className="text-xl font-bold text-slate-900 mb-6" data-id="element-22064">
                  {t('checkout.order.title')}
                </h3>

                {/* Cart Items (Collapsible) */}
                <div className="space-y-3 mb-4" data-id="element-22065">
                  {items.length > 0 ? items.map((item, idx) => {
                  const isExpanded = items.length === 1 || expandedItems.has(item.id);
                  return <motion.div key={item.id} initial={{
                    opacity: 0,
                    y: 8
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    x: -20
                  }} transition={{
                    delay: idx * 0.05
                  }} className={`bg-gradient-to-br from-indigo-50 to-slate-50 rounded-xl border border-indigo-100 relative group overflow-hidden ${items.length > 1 ? 'cursor-pointer hover:border-indigo-200' : ''}`} onClick={() => toggleCartItem(item.id)} data-id="element-22066">
                          {/* Collapsed Header */}
                          <div className="p-3.5 flex items-center justify-between gap-3" data-id="element-22067">
                            <div className="flex items-center gap-2.5 min-w-0 flex-1" data-id="element-22068">
                              <div className="w-2 h-2 rounded-full bg-indigo-400 flex-shrink-0" data-id="element-22069" />
                              <h4 className="font-bold text-slate-900 text-sm truncate" data-id="element-22070">
                                {item.name}
                              </h4>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0" data-id="element-22071">
                              <span className="text-sm font-bold text-slate-900 tabular-nums" data-id="element-22072">
                                {formatPrice(item.price)}
                              </span>
                              <button type="button" onClick={e => {
                          e.stopPropagation();
                          removeItem(item.id);
                        }} className="w-6 h-6 rounded-full flex items-center justify-center text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors" aria-label={t('checkout.delete')} data-id="element-22073">
                                <Trash2 className="w-3 h-3" data-id="element-22074" />
                              </button>
                              {items.length > 1 && <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} data-id="element-22075" />}
                            </div>
                          </div>

                          {/* Expanded Content */}
                          <AnimatePresence initial={false} data-id="element-22076">
                            {isExpanded && <motion.div initial={{
                        height: 0,
                        opacity: 0
                      }} animate={{
                        height: 'auto',
                        opacity: 1
                      }} exit={{
                        height: 0,
                        opacity: 0
                      }} transition={{
                        duration: 0.2,
                        ease: 'easeInOut'
                      }} data-id="element-22077">
                                <div className="px-4 pb-4 pt-1 border-t border-indigo-100/50" data-id="element-22078">
                                  <div className="flex items-center gap-2 mb-2" data-id="element-22079">
                                    {item.badges?.map((badge, i) => <Badge key={i} variant={badge.variant as any} className="text-[10px]" data-id="element-22080">
                                        {badge.label}
                                      </Badge>)}
                                  </div>

                                  <p className="text-[11px] text-slate-500 leading-relaxed line-clamp-2 mb-2" data-id="element-22081">
                                    {item.description}
                                  </p>

                                  {(item.format || item.duration) && <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500" data-id="element-22082">
                                      {item.format && <span className="flex items-center" data-id="element-22083">
                                          <Video className="w-3 h-3 mr-1 text-indigo-400" data-id="element-22084" />
                                          {item.format}
                                        </span>}
                                      {item.duration && <span className="flex items-center" data-id="element-22085">
                                          <Clock className="w-3 h-3 mr-1 text-indigo-400" data-id="element-22086" />
                                          {item.duration}
                                        </span>}
                                    </div>}

                                  {item.astrologer && <div className="flex items-center gap-2 mt-3 pt-2 border-t border-indigo-100/80" data-id="element-22087">
                                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700 flex-shrink-0" data-id="element-22088">
                                        {item.astrologer.initials}
                                      </div>
                                      <div className="text-xs font-medium text-slate-700" data-id="element-22089">
                                        {item.astrologer.name}
                                      </div>
                                      <div className="flex items-center gap-0.5 ml-auto" data-id="element-22090">
                                        <Star className="w-3 h-3 text-amber-400 fill-amber-400" data-id="element-22091" />
                                        <span className="text-[11px] font-medium text-slate-600" data-id="element-22092">
                                          {item.astrologer.rating}
                                        </span>
                                      </div>
                                    </div>}

                                  {!item.astrologer && <div className="flex items-center gap-2 mt-3 pt-2 border-t border-indigo-100/80" data-id="element-22093">
                                      <div className="w-7 h-7 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-22094">
                                        <FileText className="w-3.5 h-3.5 text-indigo-700" data-id="element-22095" />
                                      </div>
                                      <div className="text-xs font-medium text-slate-700" data-id="element-22096">
                                        {t('checkout.order.autoGeneration')}
                                      </div>
                                    </div>}
                                </div>
                              </motion.div>}
                          </AnimatePresence>
                        </motion.div>;
                }) : <div className="text-center py-6" data-id="element-22097">
                      <ShoppingCart className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-22098" />
                      <p className="text-sm text-slate-400" data-id="element-22099">
                        {t('checkout.order.empty')}
                      </p>
                    </div>}

                  <button type="button" onClick={() => onNavigate('marketplace')} className="w-full py-2.5 px-4 rounded-xl border-2 border-dashed border-slate-200 text-sm text-slate-500 hover:border-indigo-300 hover:text-indigo-600 transition-colors flex items-center justify-center gap-2" data-id="element-22100">
                    <span className="text-lg leading-none" data-id="element-22101">+</span>
                    {t('checkout.order.addMore')}
                  </button>
                </div>

                {/* Selected Add-ons */}
                {selectedAddOns.length > 0 && <div className="mb-4 pb-4 border-b border-slate-100 space-y-3" data-id="element-22102">
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-22103">
                      {t('checkout.order.addonsLabel')}
                    </div>
                    {ALL_FLAT_SERVICES.filter(s => selectedAddOns.includes(s.id)).map(addon => {
                  return <div key={addon.id} className="flex items-center justify-between" data-id="element-22104">
                          <div className="flex items-center gap-2" data-id="element-22105">
                            <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" data-id="element-22106" />
                            <span className="text-sm text-slate-700" data-id="element-22107">
                              {addon.title}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-slate-700 tabular-nums whitespace-nowrap" data-id="element-22108">
                            +{formatPrice(addon.price)}
                          </span>
                        </div>;
                })}
                  </div>}

                {/* Promo Code */}
                <div className="mb-6" data-id="element-22109">
                  <div className="flex items-center gap-2" data-id="element-22110">
                    <div className="flex-1" data-id="element-22111">
                      <Input placeholder={t('checkout.promo.placeholder')} value={promoCode} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPromoCode(e.target.value)} disabled={promoApplied} data-id="element-22112" />
                    </div>
                    <Button variant="outline" type="button" onClick={handleApplyPromo} disabled={promoApplied || !promoCode.trim()} className="flex-shrink-0 h-[42px]" data-id="element-22113">
                      {promoApplied ? t('checkout.promo.applied') : t('checkout.promo.apply')}
                    </Button>
                  </div>
                  {promoApplied && <motion.p initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="text-xs text-emerald-600 mt-2 flex items-center" data-id="element-22114">
                      <Gift className="w-3 h-3 mr-1" data-id="element-22115" />{' '}
                      {t('checkout.promo.success')}
                    </motion.p>}
                </div>

                {/* Package Payment Block */}
                {isAstrologer && items.every(item => item.type === 'premium-report' || item.type === 'consultation') && <>
                      {hasActivePackage && canPayWithPackage ? <div className="mb-6 p-4 bg-indigo-50 border border-indigo-100 rounded-xl" data-id="element-22116">
                          <div className="flex items-start gap-3" data-id="element-22117">
                            <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center text-indigo-600 flex-shrink-0 mt-0.5" data-id="element-22118">
                              <Package className="w-4 h-4" data-id="element-22119" />
                            </div>
                            <div className="w-full" data-id="element-22120">
                              <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-22121">
                                Этот отчет будет списан из пакета
                              </h4>
                              <div className="space-y-1 mb-3" data-id="element-22122">
                                <div className="flex justify-between text-xs" data-id="element-22123">
                                  <span className="text-indigo-700" data-id="element-22124">
                                    Пакет:
                                  </span>
                                  <span className="font-bold text-indigo-900" data-id="element-22125">
                                    {getOldestActivePackage()?.title || 'Активный пакет'}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs" data-id="element-22126">
                                  <span className="text-indigo-700" data-id="element-22127">
                                    Осталось:
                                  </span>
                                  <span className="font-bold text-indigo-900" data-id="element-22128">
                                    {totalRemaining}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs" data-id="element-22129">
                                  <span className="text-indigo-700" data-id="element-22130">
                                    После заказа:
                                  </span>
                                  <span className="font-bold text-indigo-900" data-id="element-22131">
                                    {totalRemaining - items.length}
                                  </span>
                                </div>
                              </div>

                              <label className="flex items-start gap-2 cursor-pointer group pt-2 border-t border-indigo-100/50" data-id="element-22132">
                                <div className="relative flex items-center justify-center mt-0.5" data-id="element-22133">
                                  <input type="checkbox" className="peer sr-only" checked={payWithPackage} onChange={e => setPayWithPackage(e.target.checked)} data-id="element-22134" />
                                  <div className="w-4 h-4 border-2 border-indigo-300 rounded peer-checked:bg-indigo-600 peer-checked:border-indigo-600 transition-colors" data-id="element-22135" />
                                  <Check className="w-3 h-3 text-white absolute opacity-0 peer-checked:opacity-100 transition-opacity" data-id="element-22136" />
                                </div>
                                <div className="text-sm text-indigo-900 font-medium group-hover:text-indigo-700 transition-colors" data-id="element-22137">
                                  Использовать пакет для оплаты
                                </div>
                              </label>
                            </div>
                          </div>
                        </div> : <div className="mb-6 p-4 bg-emerald-50 border border-emerald-100 rounded-xl" data-id="element-22138">
                          <div className="flex items-start gap-3" data-id="element-22139">
                            <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-600 flex-shrink-0 mt-0.5" data-id="element-22140">
                              <Package className="w-4 h-4" data-id="element-22141" />
                            </div>
                            <div className="w-full" data-id="element-22142">
                              <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-22143">
                                Пакет выгоднее
                              </h4>
                              <div className="space-y-1 mb-3" data-id="element-22144">
                                <div className="flex justify-between text-xs" data-id="element-22145">
                                  <span className="text-emerald-700" data-id="element-22146">
                                    Цена разовая:
                                  </span>
                                  <span className="font-bold text-emerald-900" data-id="element-22147">
                                    {formatPrice(basePrice)}
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs" data-id="element-22148">
                                  <span className="text-emerald-700" data-id="element-22149">
                                    Цена в пакете:
                                  </span>
                                  <span className="font-bold text-emerald-900" data-id="element-22150">
                                    от 3 600 ₽
                                  </span>
                                </div>
                                <div className="flex justify-between text-xs" data-id="element-22151">
                                  <span className="text-emerald-700" data-id="element-22152">
                                    Ваша цена по подписке:
                                  </span>
                                  <span className="font-bold text-emerald-900" data-id="element-22153">
                                    от 3 240 ₽
                                  </span>
                                </div>
                              </div>
                              <Button variant="primary" size="sm" fullWidth className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onNavigate('pricing')} data-id="element-22154">
                                Купить пакет
                              </Button>
                            </div>
                          </div>
                        </div>}
                    </>}

                {/* Totals */}
                <div className="space-y-2.5 mb-6" data-id="element-22155">
                  {items.length > 0 && <div className="flex justify-between text-sm text-slate-600" data-id="element-22156">
                      <span data-id="element-22157">
                        {items.length > 1 ? t('checkout.total.services', {
                      count: String(items.length)
                    }) : t('checkout.total.service')}
                      </span>
                      <span className="tabular-nums" data-id="element-22158">
                        {formatPrice(basePrice)}
                      </span>
                    </div>}
                  {addOnsTotal > 0 && <div className="flex justify-between text-sm text-slate-600" data-id="element-22159">
                      <span data-id="element-22160">
                        {t('checkout.total.addons', {
                      count: String(selectedAddOns.length)
                    })}
                      </span>
                      <span className="tabular-nums" data-id="element-22161">
                        +{formatPrice(addOnsTotal)}
                      </span>
                    </div>}
                  {subDiscountAmount > 0 && <div className="flex justify-between text-sm text-emerald-600" data-id="element-22162">
                      <span className="flex items-center gap-1" data-id="element-22163">
                        <Crown className="w-3.5 h-3.5" data-id="element-22164" />
                        Скидка {subDiscount}% ({currentPlan})
                      </span>
                      <span className="tabular-nums font-medium" data-id="element-22165">
                        −{formatPrice(subDiscountAmount)}
                      </span>
                    </div>}
                  {promoDiscountAmount > 0 && <div className="flex justify-between text-sm text-emerald-600" data-id="element-22166">
                      <span data-id="element-22167">{t('checkout.total.discount')}</span>
                      <span className="tabular-nums" data-id="element-22168">
                        −{formatPrice(promoDiscountAmount)}
                      </span>
                    </div>}
                  <div className="flex justify-between items-center pt-3 border-t border-slate-200" data-id="element-22169">
                    <span className="text-lg font-bold text-slate-900" data-id="element-22170">
                      {t('checkout.total.total')}
                    </span>
                    <span className="text-2xl font-bold text-indigo-600 tabular-nums" data-id="element-22171">
                      {payWithPackage ? '0 ₽' : formatPrice(total)}
                    </span>
                  </div>
                </div>

                {/* Payment hint */}
                {items.length > 1 && <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-50 border border-amber-200/60 mb-5" data-id="element-22172">
                    <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" data-id="element-22173" />
                    <p className="text-xs text-amber-700 leading-relaxed" data-id="element-22174">
                      {t('checkout.payment.hint')}
                    </p>
                  </div>}

                {/* Agreement */}
                <label className="flex items-start gap-3 mb-6 cursor-pointer group" data-id="element-22175">
                  <input type="checkbox" checked={agreedToTerms} onChange={e => setAgreedToTerms(e.target.checked)} className="mt-0.5 w-4 h-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500" data-id="element-22176" />
                  <span className="text-xs text-slate-500 leading-relaxed group-hover:text-slate-700 transition-colors" data-id="element-22177">
                    {t('checkout.agree.text')}{' '}
                    <button type="button" className="text-indigo-600 hover:underline" data-id="element-22178">
                      {t('checkout.agree.terms')}
                    </button>{' '}
                    {t('checkout.agree.and')}{' '}
                    <button type="button" className="text-indigo-600 hover:underline" data-id="element-22179">
                      {t('checkout.agree.privacy')}
                    </button>
                  </span>
                </label>

                {/* Pay Button */}
                <Button type="submit" size="lg" fullWidth disabled={isProcessing || !agreedToTerms || !isLoggedIn} icon={isProcessing ? undefined : <Lock className="w-4 h-4" data-id="element-22181" />} className="shadow-md shadow-indigo-500/20" data-id="element-22180">
                  {isProcessing ? t('checkout.pay.processing') : !isLoggedIn ? t('checkout.pay.loginRequired') : payWithPackage ? 'Оплатить из пакета' : t('checkout.pay.button', {
                  price: formatPrice(total)
                })}
                </Button>

                {!isLoggedIn && <p className="text-[10px] text-amber-600 text-center mt-2" data-id="element-22182">
                    {t('checkout.login.required')}
                  </p>}
                {isLoggedIn && !agreedToTerms && <p className="text-[10px] text-amber-600 text-center mt-2" data-id="element-22183">
                    {t('checkout.agree.required')}
                  </p>}

                {/* Trust Badges */}
                <div className="mt-6 pt-4 border-t border-slate-100 space-y-2.5" data-id="element-22184">
                  <div className="flex items-center text-xs text-slate-500" data-id="element-22185">
                    <Shield className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" data-id="element-22186" />
                    {t('checkout.trust.ssl')}
                  </div>
                  <div className="flex items-center text-xs text-slate-500" data-id="element-22187">
                    <CheckCircle2 className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" data-id="element-22188" />
                    {t('checkout.trust.refund')}
                  </div>
                  <div className="flex items-center text-xs text-slate-500" data-id="element-22189">
                    <Lock className="w-4 h-4 mr-2 text-emerald-500 flex-shrink-0" data-id="element-22190" />
                    {t('checkout.trust.noSave')}
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </form>
      </div>

      {/* Mobile Sticky CTA */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between lg:hidden z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.08)]" data-id="element-22191">
        <div data-id="element-22192">
          <div className="text-xs text-slate-500" data-id="element-22193">
            {t('checkout.mobile.total')}
          </div>
          <div className="text-xl font-bold text-slate-900" data-id="element-22194">
            {payWithPackage ? '0 ₽' : formatPrice(total)}
          </div>
        </div>
        <Button size="md" onClick={() => {
        if (agreedToTerms && isLoggedIn) {
          setIsProcessing(true);
          setTimeout(() => {
            setIsProcessing(false);
            if (payWithPackage && canPayWithPackage) {
              const pkg = getOldestActivePackage();
              if (pkg) {
                items.forEach(item => {
                  useReport(pkg.id, {
                    orderId: `ord-${Date.now()}`,
                    reportType: item.type === 'consultation' ? 'consultation' : 'report',
                    reportTitle: item.name,
                    source: item.astrologer ? 'astrologer' : 'ai',
                    astrologerName: item.astrologer?.name
                  });
                });
                success('Оплачено из пакета отчетов');
              }
            } else {
              success(t('checkout.successMobile'));
            }
            clearCart();
            onNavigate('order-confirmation');
          }, 1500);
        }
      }} disabled={isProcessing || !agreedToTerms || !isLoggedIn} className="shadow-md shadow-indigo-500/20" data-id="element-22195">
          {isProcessing ? t('checkout.pay.processing') : payWithPackage ? 'Оплатить из пакета' : t('checkout.mobile.pay')}
        </Button>
      </div>
    </div>;
}